import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './auth';
import { fetchPanelAccess, getCachedPanelAccess, clearPanelAccessCache } from './panelAccess';
import { PanelAccessContext } from './PanelAccessContext';
import {
  canAccessPanelTab,
  filterNavGroups,
  sectionFromMenuId,
} from './adminPanelTabs';
import './AdminDashboard.css';
import './AdminDashboardOverrides.css';
import './AdminAppleTheme.css';
import './DashboardHome.css';
import './ChecklistShiftPages.css';
import logo from './assets/newlogo.png';
import ChecklistPage from './ChecklistPage';
import ShiftSchedulerPage from './ShiftSchedulerPage';
import AppCalendarEventsPage from './AppCalendarEventsPage';
import QRCodesPage from './QRCodesPage';
import OrdersPage from './OrdersPage';
import DashboardShopsMap from './DashboardShopsMap';
import {
  getUserInitial,
  getGreeting,
  getFirstName,
} from './dashboard/DashboardUI';
import StaffDirectoryPage from './StaffDirectoryPage';
import ShopsPage from './ShopsPage';
import BranchAssignmentPage from './BranchAssignmentPage';
import EkipSectionTabs, { getEkipSubTab } from './EkipSectionTabs';
import AdsAdminPage from './AdsAdminPage';
import UsersAdminPage from './UsersAdminPage';
import StampCardsPage from './StampCardsPage';
import SimpraAdminPage from './SimpraAdminPage';
import YetkilendirmePage from './YetkilendirmePage';
import AnalyticsPage from './AnalyticsPage';
import SettingsPage from './SettingsPage';
import MediaAdminPage from './MediaAdminPage';
import SidebarCollapsibleGroup from './SidebarCollapsibleGroup';
import AdminHeaderSearch from './AdminHeaderSearch';
import { useShopsFromDb } from './useShopsFromDb';
import { applyTheme, readStored, THEME_STORAGE_KEY } from './adminTheme';

/** Sol menü: kafe yönetimi · uygulama yönetimi */
const NAV_GROUPS = [
  {
    id: 'cafe',
    title: 'Kafe yönetimi',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <path d="M9 22V12h6v10" />
          </svg>
        ),
      },
      {
        id: 'users',
        label: 'Kullanıcılar',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 00-3-3.87" />
            <path d="M16 3.13a4 4 0 010 7.75" />
          </svg>
        ),
      },
      {
        id: 'stamp-cards',
        label: 'Damga kartları',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <circle cx="8" cy="12" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="16" cy="12" r="1.5" />
          </svg>
        ),
      },
      {
        id: 'yetkilendirme',
        label: 'Yetkilendirme',
        ownerOnly: true,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
        ),
      },
      {
        id: 'ekip',
        label: 'Ekip',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
          </svg>
        ),
      },
      {
        id: 'checklist',
        label: 'Günlük checklist',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
          </svg>
        ),
      },
      {
        id: 'shifts',
        label: 'Shift düzenleyici',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'app',
    title: 'Uygulama yönetimi',
    items: [
      {
        id: 'shops',
        label: 'Mağazalar',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        ),
      },
      {
        id: 'ads',
        label: 'Reklamlar',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <rect x="3" y="3" width="18" height="14" rx="2" />
            <path d="M7 21h10M12 17v4" />
          </svg>
        ),
      },
      {
        id: 'media',
        label: 'Medya',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        ),
      },
      {
        id: 'app-calendar-events',
        label: 'Uygulama takvimi',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
            <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
          </svg>
        ),
      },
      {
        id: 'qr-codes',
        label: 'QR Codes',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <path d="M14 14h2v2h-2zM18 14h3M14 18v3M18 18h3v3h-3z" />
          </svg>
        ),
      },
      {
        id: 'orders',
        label: 'Siparişler',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M6 2h12l1 5H5l1-5z" />
            <path d="M5 7h14l-1.2 13.2A2 2 0 0115.81 22H8.19a2 2 0 01-1.99-1.8L5 7z" />
            <path d="M9 11v4M15 11v4" />
          </svg>
        ),
      },
      {
        id: 'simpra',
        label: 'Simpra',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'account',
    title: 'Hesap',
    items: [
      {
        id: 'settings',
        label: 'Ayarlar',
        alwaysShow: true,
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09A1.65 1.65 0 0015 4.6a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
          </svg>
        ),
      },
    ],
  },
];

function isNavItemActive(item, activeMenu, pathname, search) {
  if (item.path) return pathname === item.path;
  if (item.id === 'ekip') {
    return activeMenu === 'ekip';
  }
  if (item.id === 'shops') {
    return activeMenu === 'shops';
  }
  return activeMenu === item.id;
}

function renderNavItem(item, activeMenu, pathname, search, onNavClick) {
  const active = isNavItemActive(item, activeMenu, pathname, search);
  return (
    <button
      key={item.id}
      type="button"
      className={[
        'nav-item',
        item.subOf ? 'nav-item--sub' : '',
        active ? 'active' : '',
      ].filter(Boolean).join(' ')}
      aria-current={active ? 'page' : undefined}
      onClick={() => onNavClick(item)}
    >
      {item.icon || <span className="nav-item-sub-dot" aria-hidden />}
      {item.label}
    </button>
  );
}

function renderNavGroupItems(items, activeMenu, pathname, search, onNavClick) {
  const nodes = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item.subOf) continue;

    const subs = [];
    let j = i + 1;
    while (j < items.length && items[j].subOf === item.id) {
      subs.push(items[j]);
      j += 1;
    }

    nodes.push(
      <div key={item.id} className="nav-item-block">
        {renderNavItem(item, activeMenu, pathname, search, onNavClick)}
        {subs.length > 0 ? (
          <div className="nav-subgroup" role="group" aria-label={`${item.label} alt menü`}>
            {subs.map((sub) => renderNavItem(sub, activeMenu, pathname, search, onNavClick))}
          </div>
        ) : null}
      </div>,
    );
    i = j - 1;
  }
  return nodes;
}

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { shops: dashboardShops, loading: shopsMapLoading } = useShopsFromDb();
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [userEmail, setUserEmail] = useState('');
  const [panelAccess, setPanelAccess] = useState(() => getCachedPanelAccess());
  const panelAccessReady = panelAccess != null;

  useEffect(() => {
    applyTheme(readStored(THEME_STORAGE_KEY, 'light'));
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserEmail(data?.user?.email ?? ''));
    let cancelled = false;
    fetchPanelAccess(supabase).then((access) => {
      if (!cancelled) setPanelAccess(access);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    const view = params.get('view');

    if (section === 'shop-assignments' || (section === 'shops' && view === 'assignments')) {
      navigate({ pathname: '/dashboard', search: '?section=ekip&view=assignments' }, { replace: true });
      return;
    }

    if (section === 'ekip' && view === 'managers') {
      navigate({ pathname: '/dashboard', search: '?section=ekip' }, { replace: true });
      return;
    }

    if (section === 'analytics') {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (section === 'ekip') setActiveMenu('ekip');
    else if (section === 'shops') setActiveMenu('shops');
    else if (section === 'ads') setActiveMenu('ads');
    else if (section === 'media') setActiveMenu('media');
    else if (section === 'settings') setActiveMenu('settings');
    else if (section === 'app-calendar') setActiveMenu('app-calendar-events');
    else if (section === 'users') setActiveMenu('users');
    else if (section === 'stamp-cards') setActiveMenu('stamp-cards');
    else if (section === 'checklist') setActiveMenu('checklist');
    else if (section === 'shifts') setActiveMenu('shifts');
    else if (section === 'qr-codes') setActiveMenu('qr-codes');
    else if (section === 'orders') setActiveMenu('orders');
    else if (section === 'simpra') setActiveMenu('simpra');
    else if (section === 'yetkilendirme') setActiveMenu('yetkilendirme');
    else if (!section) setActiveMenu('dashboard');
  }, [location.search, navigate]);

  const visibleNavGroups = useMemo(
    () => filterNavGroups(NAV_GROUPS, panelAccess),
    [panelAccess],
  );

  const activeSectionLabel = useMemo(() => {
    if (activeMenu === 'dashboard') return null;
    for (const group of visibleNavGroups) {
      for (const item of group.items) {
        if (item.id === activeMenu) return item.label;
      }
    }
    return null;
  }, [activeMenu, visibleNavGroups]);

  const headerSearchItems = useMemo(() => {
    const out = [];
    for (const group of visibleNavGroups) {
      for (const item of group.items) {
        if (item.subOf) continue;
        out.push({
          id: item.id,
          label: item.label,
          group: group.title,
          icon: item.icon,
        });
      }
    }
    return out;
  }, [visibleNavGroups]);

  useEffect(() => {
    if (!panelAccessReady || panelAccess.isOwner) return;
    if (activeMenu === 'settings') return;
    if (!canAccessPanelTab(panelAccess, activeMenu)) {
      const first = panelAccess.tabs[0];
      if (!first) return;
      setActiveMenu(first);
      const section = sectionFromMenuId(first);
      if (section) {
        navigate({ pathname: '/dashboard', search: `?section=${section}` }, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [panelAccess, panelAccessReady, activeMenu, navigate]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const clockLabel = now.toLocaleTimeString('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const dateLabel = now.toLocaleDateString('tr-TR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const navView = new URLSearchParams(location.search).get('view');
  const showEkipSection = activeMenu === 'ekip';
  const ekipSubTab = getEkipSubTab(navView);
  const showEkipPeople = showEkipSection && ekipSubTab === 'people';
  const showEkipAssignments = showEkipSection && ekipSubTab === 'assignments';

  const canShowMenu = (menuId) => {
    if (!panelAccessReady) return false;
    if (menuId === 'yetkilendirme') {
      return panelAccess.isOwner;
    }
    if (menuId === 'settings') return true;
    return panelAccess.isOwner || canAccessPanelTab(panelAccess, menuId);
  };

  const headerRoleLabel = !panelAccessReady
    ? '…'
    : panelAccess.isOwner
      ? 'İşletme sahibi'
      : 'Panel yetkilisi';

  const handleSignOut = async () => {
    clearPanelAccessCache();
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };

  const handleNavClick = (item, options = {}) => {
    if (item.path) {
      navigate(item.path);
      setIsSidebarHidden(true);
      return;
    }
    setActiveMenu(item.id);
    if (item.id === 'shops' && options.shopId != null) {
      navigate(
        {
          pathname: '/dashboard',
          search: `?section=shops&shop=${encodeURIComponent(String(options.shopId))}`,
        },
        { replace: true },
      );
    } else {
      const section = sectionFromMenuId(item.id);
      if (section) {
        navigate({ pathname: '/dashboard', search: `?section=${section}` }, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
    setIsSidebarHidden(true);
  };

  return (
    <PanelAccessContext.Provider value={panelAccess}>
    <div className={`admin-dashboard ${isSidebarHidden ? 'sidebar-hidden' : ''}`}>
      <div
        className={`sidebar-overlay ${!isSidebarHidden ? 'active' : ''}`}
        onClick={() => setIsSidebarHidden(true)}
        aria-hidden
      />

      <aside className={`sidebar ${isSidebarHidden ? 'hidden' : ''}`}>
        <div className="sidebar-header">
          <img src={logo} alt="" className="logo" />
        </div>
        <nav className="sidebar-nav" aria-label="Ana menü">
          {visibleNavGroups.map((group) => {
            const hasActive = group.items.some((item) =>
              isNavItemActive(item, activeMenu, location.pathname, location.search),
            );
            return (
              <SidebarCollapsibleGroup
                key={group.id}
                groupId={group.id}
                title={group.title}
                hasActiveItem={hasActive}
              >
                {renderNavGroupItems(
                  group.items,
                  activeMenu,
                  location.pathname,
                  location.search,
                  handleNavClick,
                )}
              </SidebarCollapsibleGroup>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <button type="button" className="logout-btn" onClick={handleSignOut}>
            Çıkış Yap
          </button>
        </div>
      </aside>

      <div className="main-content">
        <div className="content-header">
          <div className="content-header-left">
            <div className="content-header-datetime">
              <time className="content-header-clock" dateTime={now.toISOString()} aria-live="polite">
                {clockLabel}
              </time>
              <time className="content-header-date" dateTime={now.toISOString().slice(0, 10)}>
                {dateLabel}
              </time>
            </div>
            {activeSectionLabel ? (
              <span className="content-header-section" aria-current="page">
                {activeSectionLabel}
              </span>
            ) : null}
          </div>
          <AdminHeaderSearch
            items={headerSearchItems}
            disabled={!panelAccessReady}
            onSelect={(item) => handleNavClick({ id: item.id })}
          />
          <div className="content-header-right">
          <button
            type="button"
            className="admin-mobile-menu-btn"
            onClick={() => setIsSidebarHidden(false)}
            aria-label="Menüyü aç"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <div className="user-info user-info--premium" title={headerRoleLabel}>
            <span className="user-info-avatar" aria-hidden>
              {getUserInitial(userEmail)}
            </span>
            <span className="user-email user-email--header">{userEmail || '—'}</span>
          </div>
          </div>
        </div>

        <div className="content-body">
          {!panelAccessReady ? (
            <div className="admin-dashboard-loading" aria-live="polite">
              Yükleniyor…
            </div>
          ) : null}
          {panelAccessReady && canShowMenu('ekip') && showEkipSection && (
            <div className="admin-page-content ekip-section-shell">
              <EkipSectionTabs subTab={ekipSubTab} />
              {showEkipPeople && (
                <StaffDirectoryPage
                  unifiedEkip
                  embedded
                  staffRole="barista"
                />
              )}
              {showEkipAssignments && <BranchAssignmentPage embedded />}
            </div>
          )}
          {panelAccessReady && canShowMenu('shops') && activeMenu === 'shops' && <ShopsPage />}
          {panelAccessReady && canShowMenu('checklist') && activeMenu === 'checklist' && <ChecklistPage />}
          {panelAccessReady && canShowMenu('shifts') && activeMenu === 'shifts' && <ShiftSchedulerPage />}
          {panelAccessReady && canShowMenu('qr-codes') && activeMenu === 'qr-codes' && <QRCodesPage />}
          {panelAccessReady && canShowMenu('orders') && activeMenu === 'orders' && <OrdersPage />}
          {panelAccessReady && canShowMenu('simpra') && activeMenu === 'simpra' && <SimpraAdminPage />}
          {panelAccessReady && canShowMenu('ads') && activeMenu === 'ads' && <AdsAdminPage />}
          {panelAccessReady && canShowMenu('media') && activeMenu === 'media' && <MediaAdminPage />}
          {panelAccessReady && canShowMenu('settings') && activeMenu === 'settings' && <SettingsPage />}
          {panelAccessReady && canShowMenu('app-calendar-events') && activeMenu === 'app-calendar-events' && <AppCalendarEventsPage />}
          {panelAccessReady && canShowMenu('users') && activeMenu === 'users' && <UsersAdminPage />}
          {panelAccessReady && canShowMenu('stamp-cards') && activeMenu === 'stamp-cards' && <StampCardsPage />}
          {panelAccessReady && canShowMenu('yetkilendirme') && activeMenu === 'yetkilendirme' && <YetkilendirmePage />}

          {panelAccessReady && canShowMenu('dashboard') && activeMenu === 'dashboard' && (
            <div className="admin-page-content admin-page-content--dashboard">
              <div className="dashboard-home">
                <header className="dashboard-home-bar">
                  <div className="dashboard-home-bar-copy">
                    <p className="dashboard-home-kicker">Özet</p>
                    <h1 className="dashboard-home-title">
                      {getFirstName(userEmail)
                        ? `${getGreeting(new Date())}, ${getFirstName(userEmail)}`
                        : getGreeting(new Date())}
                    </h1>
                  </div>
                </header>
                <div className="dashboard-stage">
                  <aside className="dashboard-insights" aria-label="Analitik">
                    <AnalyticsPage embedded />
                  </aside>
                  <DashboardShopsMap
                    shops={dashboardShops}
                    loading={shopsMapLoading}
                    premium
                    onManageShops={() => handleNavClick({ id: 'shops' })}
                    onShopSelect={(shop) => handleNavClick({ id: 'shops' }, { shopId: shop.id })}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </PanelAccessContext.Provider>
  );
}

export default Dashboard;
